<script setup>
  import { isEmptyObject } from "../utils/functions"

  const props = defineProps({
    publications: {
      type: Array,
      required: true,
    },
    entity: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    isLoading: {
      required: true,
      type: Boolean,
    },
  })

  const { locale } = useI18n()
  const $route = useRoute()
  const $router = useRouter()

  const addParamsToUrl = (slug) => {
    $router.push({
      path: $route.path,
      query: {
        ...$route.query,
        category: slug,
      },
    })
  }

  const removeAllParamsFromUrl = () => {
    $router.push({ path: $route.path, query: {} })
  }

  const isDragging = ref(false)
  const startX = ref(0)
  const scrollLeft = ref(0)
  const slider = ref(null)

  const startDrag = (e) => {
    isDragging.value = true
    // Handle both mouse and touch events
    const pageX = e.type.includes('touch') ? e.touches[0].pageX : e.pageX
    startX.value = pageX - slider.value.offsetLeft
    scrollLeft.value = slider.value.scrollLeft
  }

  const onDrag = (e) => {
    if (!isDragging.value) return
    e.preventDefault()
    // Handle both mouse and touch events
    const pageX = e.type.includes('touch') ? e.touches[0].pageX : e.pageX
    const x = pageX - slider.value.offsetLeft
    const walk = (x - startX.value) * 2
    slider.value.scrollLeft = scrollLeft.value - walk
  }

  const stopDrag = () => {
    isDragging.value = false
  }
</script>

<template>
  <div class="text-center pt-8 pb-2">
    <template v-if="isLoading">
      <div class="container flex items-center justify-center space-x-4">
        <Skeleton width="6rem" shape="circle" height="6rem" />
        <Skeleton width="6rem" shape="circle" height="6rem" />
        <Skeleton width="6rem" shape="circle" height="6rem" />
      </div>
    </template>
    <template v-else>
      <div
        class="hide-scrollbar justify-center items-center max-w-full overflow-hidden overflow-x-auto mx-auto"
        ref="slider"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @touchstart="startDrag"
        @touchmove="onDrag"
        @touchend="stopDrag"
        v-if="publications?.length > 0"
      >
        <ul
          class="cursor-grab active:cursor-grabbing snap-proximity snap-x flex gap-4 md:gap-16 items-center"
          v-if="!isEmptyObject(props.entity)"
        >
          <li
            class="sticky h-40 -left-1 bg-gradient-to-r from-white dark:from-[#121212] to-transparent pointer-events-none z-10"
          >
            <div class="w-12"></div>
          </li>
          <li
            :key="Math.random()"
            v-for="entityItem in props.entity"
            class="w-24 md:w-32 flex-shrink-0"
          >
            <NuxtLink
              :to="`/${entityItem?.type}/${entityItem?.username}`"
              class="snap-always snap-center"
              custom="div"
              v-slot="{ href, navigate }"
            >
              <div :href="href" @click="navigate" class="cursor-pointer">
                <ArtistItem :artist="entityItem" />
              </div>
            </NuxtLink>
          </li>
          <li
            class="sticky h-40 -right-1 bg-gradient-to-l from-white dark:from-[#121212] to-transparent pointer-events-none z-10"
          >
            <div class="w-12"></div>
          </li>
        </ul>
      </div>

      <div
        class="container flex space-x-2 md:space-x-4 hide-scrollbar max-w-full overflow-hidden overflow-x-auto mx-auto mt-14 select-none"
        ref="slider"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @touchstart="startDrag"
        @touchmove="onDrag"
        @touchend="stopDrag"
        v-if="!isEmptyObject(props.categories)"
      >
        <div
          :class="{
            'text-sm bg-neutral-300 border border-neutral-200 dark:text-black':
              isEmptyObject($route.query),
            'text-sm bg-neutral-50 dark:bg-neutral-800 border border-neutral-200':
              !isEmptyObject($route.query),
          }"
          class="cursor-pointer text-sm border border-neutral-200 w-[90px] md:w-[120px] text-center rounded-md px-3 py-1"
          @click="removeAllParamsFromUrl"
        >
          Todos
        </div>
        <div
          v-for="category in props.categories"
          :key="category.id"
          @click="addParamsToUrl(category.slug)"
          :class="{
            'text-sm bg-neutral-300 border border-neutral-300 dark:text-black':
              $route.query.category === category.slug,
            'text-sm bg-neutral-50 dark:bg-neutral-800 border border-neutral-200':
              $route.query.category !== category.slug,
          }"
          class="w-[90px] md:w-[120px] text-center rounded-md px-3 py-1 cursor-pointer"
        >
          {{ category.label }}
        </div>
      </div>
    </template>
  </div>
</template>

<style>
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }

  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
